import React, { useCallback, useEffect, useMemo } from "react";
import { employeesFormStyles } from "../employees.styles";
import { Field, Form, Formik } from "formik";
import history from "@history";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@material-ui/core";
import LoadingButton from "app/main/shared-components/loading-button/LoadingButton";
import { toggleQuickPanel } from "app/main/shared-components/quick-panel/store";
import { useDispatch, useSelector } from "react-redux";
import { createUser } from "../store/create-user.slice";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { AppState } from "../../../store";
import { setEmployeeScopesForm } from "../store/employee-scopes-form.slice";

type ScopeItem = {
  value: string;
  labelKey: string;
  defaultLabel: string;
};

const SCOPE_ITEMS: ScopeItem[] = [
  {
    value: "FLIGHT_RESERVATION",
    labelKey: "app.main.agency.create.scopesInfo.label1",
    defaultLabel: "Booking flights",
  },
  {
    value: "ADD_TRAVELER",
    labelKey: "app.main.agency.create.scopesInfo.label2",
    defaultLabel: "Addition of passengers",
  },
  {
    value: "DEACTIVATE_TRAVELER",
    labelKey: "app.main.agency.create.scopesInfo.label3",
    defaultLabel: "Cancellation of passengers",
  },
  {
    value: "CANCELLATION_OF_RESERVATION",
    labelKey: "app.main.agency.create.scopesInfo.label15",
    defaultLabel: "Cancellation of reservation",
  },
  {
    value: "RESERVATION_DISCUSSION",
    labelKey: "app.main.agency.create.scopesInfo.label5",
    defaultLabel: "Booking note",
  },
  {
    value: "UPDATE_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label6",
    defaultLabel: "Change of passengers",
  },
  {
    value: "CONFIRM_VIBER",
    labelKey: "app.main.agency.create.scopesInfo.label4",
    defaultLabel: "Viber confirmation",
  },
  {
    value: "ACTIVITIES",
    labelKey: "app.main.agency.create.scopesInfo.label8",
    defaultLabel: "Activities",
  },
  {
    value: "ADD_DISCUSSION",
    labelKey: "app.main.agency.create.scopesInfo.label9",
    defaultLabel: "Adding remarks",
  },
  {
    value: "UPDATE_TRAVELER_PRICES",
    labelKey: "app.main.agency.create.scopesInfo.label10",
    defaultLabel: "Passenger price change",
  },
  {
    value: "SHOW_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label11",
    defaultLabel: "Show/Don't show passengers",
  },
  {
    value: "UPDATE_CONTACT",
    labelKey: "app.main.agency.create.scopesInfo.label12",
    defaultLabel: "Changing contactor",
  },
  {
    value: "DELETE_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label13",
    defaultLabel: "Deleting a segment",
  },
  {
    value: "UPDATE_TRAVELER_FLIGHTS",
    labelKey: "app.main.agency.create.scopesInfo.label14",
    defaultLabel: "Change of passenger flights",
  },
];

const handleKeyDownSpace = (e: KeyboardEvent) => {
  if (e.code === "Space") {
    e.preventDefault();
  }
};

const CreateEmployeeForm = () => {
  const classes = employeesFormStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const scopesForm = useSelector(
    (state: AppState) => state.employees.employeeScopeForm
  );

  const scopes: string[] = Array.isArray(scopesForm?.scopes)
    ? scopesForm.scopes
    : [];

  const isQuickPanelOpen = useSelector((state: any) => state.quickPanel);

  useEffect(() => {
    let unblock: any;

    if (isQuickPanelOpen) {
      unblock = history.block(() => {
        return false;
      });
    }

    return () => {
      if (unblock) {
        unblock();
      }
    };
  }, [isQuickPanelOpen]);

  const scopeValues = useMemo(() => {
    return SCOPE_ITEMS.map((scope) => scope.value);
  }, []);

  const selectedScopesCount = scopeValues.filter((scope) =>
    scopes.includes(scope)
  ).length;

  const isAllSelected =
    scopeValues.length > 0 && selectedScopesCount === scopeValues.length;

  const isPartiallySelected =
    selectedScopesCount > 0 && selectedScopesCount < scopeValues.length;

  const updateScopes = useCallback(
    (nextScopes: string[]) => {
      dispatch(
        setEmployeeScopesForm({
          scopes: nextScopes,
        })
      );
    },
    [dispatch]
  );

  const handleChange = useCallback(
    (value: string) => {
      const nextScopes = scopes.includes(value)
        ? scopes.filter((item) => item !== value)
        : [...scopes, value];

      updateScopes(nextScopes);
    },
    [scopes, updateScopes]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      const nextScopes = scopes.filter((scope) => !scopeValues.includes(scope));
      updateScopes(nextScopes);
      return;
    }

    const nextScopes = Array.from(new Set([...scopes, ...scopeValues]));
    updateScopes(nextScopes);
  }, [isAllSelected, scopes, scopeValues, updateScopes]);

  const renderScopeCheckbox = (scope: ScopeItem) => {
    return (
      <label key={scope.value}>
        <Field
          type="checkbox"
          name={scope.value}
          checked={scopes.includes(scope.value)}
          value={scope.value}
          onChange={() => handleChange(scope.value)}
        />
        {t(scope.labelKey, scope.defaultLabel)}
      </label>
    );
  };

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(
        t(
          "app.main.employee.createEmployee.nameValidation",
          "Please enter the required field"
        )
      )
      .matches(
        /^[aA-zZ\s]+$/,
        t(
          "app.main.costumer.createCostumer.registerCostumer.fullName.title",
          "Only alphabets are allowed for this field "
        )
      ),

    surname: yup
      .string()
      .required(
        t(
          "app.main.employee.createEmployee.surnameValidation",
          "Please enter the required field"
        )
      )
      .matches(
        /^[aA-zZ\s]+$/,
        t(
          "app.main.costumer.createCostumer.registerCostumer.fullName.title",
          "Only alphabets are allowed for this field "
        )
      ),

    email: yup
      .string()
      .email()
      .required(
        t(
          "app.main.costumer.createCostumer.registerCostumer.email",
          "Please enter the required field"
        )
      ),

    password: yup
      .string()
      .required(
        t(
          "app.main.costumer.createCostumer.registerCostumer.password",
          "Please enter the required field"
        )
      ),

    role: yup
      .string()
      .required(
        t(
          "app.main.employee.createEmployee.roleValidation",
          "Please enter the required field"
        )
      ),
  });

  return (
    <div className={classes.formWrapper}>
      <div className={classes.formHeader}>
        <h1 style={{ color: "#0D3B66" }}>
          {t("app.main.employees.create.title", "Register Employee")}
        </h1>
      </div>

      <Formik
        initialValues={{
          name: "",
          surname: "",
          email: "",
          contactNumber: "",
          address: "",
          password: "",
          role: "",
          status: false,
          scopes: [],
        }}
        validationSchema={validationSchema}
        onSubmit={(data) => {
          const { name, surname, email, password, role } = data;

          dispatch(
            createUser({
              body: {
                fullName: `${name} ${surname}`,
                email,
                password,
                role,
                status: true,
                scopes,
              },
            })
          );
        }}
      >
        {({ values, errors, touched, setValues }) => (
          <Form>
            <div className={classes.row}>
              <Field
                variant="outlined"
                value={values.name}
                name="name"
                label={t(
                  "app.main.employees.create.employeeName",
                  "Employee name"
                )}
                as={TextField}
                onKeyDown={handleKeyDownSpace}
                required
                error={Boolean(errors.name && touched.name)}
                helperText={touched.name ? errors.name : ""}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    name: e.target.value,
                  });
                }}
              />

              <Field
                variant="outlined"
                value={values.surname}
                name="surname"
                label={t(
                  "app.main.employees.create.employeeLastname",
                  "Employee lastname"
                )}
                as={TextField}
                onKeyDown={handleKeyDownSpace}
                required
                error={Boolean(errors.surname && touched.surname)}
                helperText={touched.surname ? errors.surname : ""}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    surname: e.target.value,
                  });
                }}
              />
            </div>

            <div className={classes.row}>
              <Field
                variant="outlined"
                type="email"
                value={values.email}
                onKeyDown={handleKeyDownSpace}
                name="email"
                label={t("app.main.employees.employeeList.table.email", "Email")}
                as={TextField}
                required
                error={Boolean(errors.email && touched.email)}
                helperText={touched.email ? errors.email : ""}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    email: e.target.value,
                  });
                }}
              />

              <Field
                variant="outlined"
                type="password"
                value={values.password}
                name="password"
                label={t("app.main.employees.create.password", "Password")}
                as={TextField}
                required
                error={Boolean(errors.password && touched.password)}
                helperText={touched.password ? errors.password : ""}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    password: e.target.value,
                  });
                }}
              />
            </div>

            <div className={classes.row} style={{ width: "50%" }}>
              <Field
                variant="outlined"
                value={values.role}
                name="role"
                required
                label={t("app.main.employees.create.dropdown.title", "Role")}
                select
                as={TextField}
                error={Boolean(errors.role && touched.role)}
                helperText={touched.role ? errors.role : ""}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    role: e.target.value,
                  });
                }}
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="CALL_AGENT">Call Agent</MenuItem>
              </Field>
            </div>

            <div
              className={classes.row}
              style={{
                width: "100%",
                display: "block",
                marginTop: "20px",
              }}
            >
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    backgroundColor: "lightgrey",
                    padding: "15px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                                    <h2
                    style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#000",
                    }}
                    >
                    {t(
                        "app.main.agency.agentsList.agentEdit.sidebar.title2",
                        "Employee privileges"
                    )}
                    </h2>

                  <FormControlLabel
                    style={{ marginRight: 0 }}
                    control={
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartiallySelected}
                        onChange={handleSelectAll}
                        color="primary"
                      />
                    }
                    label={t(
                      "app.main.agency.create.scopesInfo.selectAll",
                      "Select All"
                    )}
                  />
                </div>

                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderTop: "none",
                    padding: "18px 15px",
                  }}
                >
                  <Grid container spacing={4}>
                    <Grid item xs={3}>
                      <div className={classes.checkboxGroup} role="group">
                        {SCOPE_ITEMS.slice(0, 4).map(renderScopeCheckbox)}
                      </div>
                    </Grid>

                    <Grid item xs={3}>
                      <div className={classes.checkboxGroup} role="group">
                        {SCOPE_ITEMS.slice(4, 8).map(renderScopeCheckbox)}
                      </div>
                    </Grid>

                    <Grid item xs={3}>
                      <div className={classes.checkboxGroup} role="group">
                        {SCOPE_ITEMS.slice(8, 11).map(renderScopeCheckbox)}
                      </div>
                    </Grid>

                    <Grid item xs={3}>
                      <div className={classes.checkboxGroup} role="group">
                        {SCOPE_ITEMS.slice(11).map(renderScopeCheckbox)}
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </div>

            <div className={classes.row}>
              <LoadingButton
                type="submit"
                btnTitle={t(
                  "app.main.schedules.update.form.submitButton",
                  "Submit"
                )}
                isLoading={false}
              />

              <Button
                onClick={() => {
                  dispatch(toggleQuickPanel());
                  history.push("/employees/employees-list");
                }}
              >
                {t("app.main.schedules.splitPassenger.closeButton", "Close")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateEmployeeForm;